import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    X,
    Save,
    Loader2,
    UploadCloud,
    FileText,
    Download,
} from "lucide-react";
import { toast } from "sonner";
import { crud, absoluteUrl } from "../lib/api";
import { EVRAK_TURLERI, EVRAK_ONAY_DURUMU } from "../lib/choices";
import { detectEvrakTuruFromFilename, evrakTuruLabel } from "../lib/evrakTuruDetect";
import SearchableSelect from "./SearchableSelect";
import { fkId } from "../lib/fkId";
import {
    PANEL_INPUT,
    PANEL_MODAL_SHELL,
    PANEL_SELECT,
} from "../lib/panelFormStyles";

/**
 * Evraklar modülü için sade form: sigorta dosyası + tür + onay + dosya yükleme.
 */
export default function EvrakFormModal({
    open,
    title,
    initial,
    onClose,
    onSaved,
}) {
    const isEdit = !!initial?.id;
    const inputRef = useRef(null);

    const [sigortaDosyasi, setSigortaDosyasi] = useState(null);
    const [evrakTuru, setEvrakTuru] = useState("RUHSAT");
    const [onayDurumu, setOnayDurumu] = useState("BEKLIYOR");
    const [redNedeni, setRedNedeni] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const mevcutDosyaUrl = useMemo(() => {
        const raw = initial?.dosya || initial?.dosya_url;
        return raw ? absoluteUrl(raw) : null;
    }, [initial]);

    useEffect(() => {
        if (!open) return;
        setErr(null);
        setFile(null);
        setSigortaDosyasi(
            fkId(initial?.sigorta_dosyasi) ?? fkId(initial?.sigorta_dosya) ?? null,
        );
        setEvrakTuru(initial?.evrak_turu || initial?.tur || "RUHSAT");
        setOnayDurumu(initial?.onay_durumu || "BEKLIYOR");
        setRedNedeni(initial?.red_nedeni || "");
    }, [open, initial]);

    if (!open) return null;

    async function handleSubmit(e) {
        e.preventDefault();
        setErr(null);

        if (!sigortaDosyasi) {
            setErr("Sigorta dosyası seçin.");
            return;
        }
        if (!isEdit && !file) {
            setErr("Yüklenecek dosya seçin.");
            return;
        }
        if (onayDurumu === "REDDEDILDI" && !redNedeni.trim()) {
            setErr("Reddedilen evrak için red nedeni yazın.");
            return;
        }

        setBusy(true);
        try {
            const meta = {
                sigorta_dosyasi: sigortaDosyasi,
                evrak_turu: evrakTuru,
                onay_durumu: onayDurumu,
                red_nedeni: onayDurumu === "REDDEDILDI" ? redNedeni.trim() : "",
            };

            if (isEdit) {
                if (file) {
                    await crud.uploadPatch("evraklar", initial.id, {
                        ...meta,
                        dosya: file,
                    });
                } else {
                    await crud.update("evraklar", initial.id, meta);
                }
                toast.success("Evrak güncellendi.");
            } else {
                await crud.upload("evraklar", { ...meta, dosya: file });
                toast.success("Evrak yüklendi.");
            }
            onSaved?.();
        } catch (e) {
            const data = e?.response?.data;
            let msg;
            if (data && typeof data === "object") {
                msg = Object.entries(data)
                    .map(
                        ([k, v]) =>
                            `${k}: ${Array.isArray(v) ? v.join(", ") : v}`,
                    )
                    .join(" · ");
            } else {
                msg = data?.detail || e?.message || "Kayıt başarısız.";
            }
            setErr(msg);
        } finally {
            setBusy(false);
        }
    }

    const modal = (
        <div
            className="fixed inset-0 z-[80] overflow-y-auto"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="fixed inset-0 bg-black/70 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />
            <div className="relative min-h-full flex items-start justify-center px-4 py-8">
                <div className={`${PANEL_MODAL_SHELL} max-w-lg`}>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-md bg-[var(--mbs-primary-soft)] border border-[rgba(15,118,110,0.30)] flex items-center justify-center">
                                <FileText className="h-4 w-4 text-[var(--mbs-primary-hover)]" />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mbs-ink-3)]">
                                    {isEdit ? "Düzenle" : "Yeni evrak"}
                                </div>
                                <div className="font-display text-lg text-[var(--mbs-ink)]">
                                    {title}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-8 w-8 rounded-md hover:bg-[var(--mbs-bg-soft)] flex items-center justify-center text-[var(--mbs-ink-3)]"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-5 space-y-5">
                        {err && (
                            <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/25 rounded-md px-3 py-2">
                                {err}
                            </div>
                        )}

                        <fieldset className="space-y-3">
                            <legend className="text-[10px] uppercase tracking-[0.16em] text-[var(--mbs-primary-hover)] font-semibold">
                                Bağlantı
                            </legend>
                            <label className="block">
                                <span className="text-xs text-[var(--mbs-ink-3)] mb-1 block">
                                    Sigorta dosyası *
                                </span>
                                <SearchableSelect
                                    value={sigortaDosyasi}
                                    onChange={setSigortaDosyasi}
                                    resource="sigorta-dosyalari"
                                    placeholder="Dosya ara (no, plaka, müvekkil)..."
                                    testId="evrak-form-sigorta"
                                />
                            </label>
                        </fieldset>

                        <fieldset className="space-y-3">
                            <legend className="text-[10px] uppercase tracking-[0.16em] text-[var(--mbs-primary-hover)] font-semibold">
                                Evrak bilgisi
                            </legend>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-xs text-[var(--mbs-ink-3)] mb-1 block">
                                        Evrak türü *
                                    </span>
                                    <select
                                        value={evrakTuru}
                                        onChange={(e) => setEvrakTuru(e.target.value)}
                                        className={PANEL_SELECT}
                                    >
                                        {EVRAK_TURLERI.map((t) => (
                                            <option key={t.value} value={t.value}>
                                                {t.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-xs text-[var(--mbs-ink-3)] mb-1 block">
                                        Onay durumu
                                    </span>
                                    <select
                                        value={onayDurumu}
                                        onChange={(e) => setOnayDurumu(e.target.value)}
                                        className={PANEL_SELECT}
                                    >
                                        {EVRAK_ONAY_DURUMU.map((t) => (
                                            <option key={t.value} value={t.value}>
                                                {t.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                            {onayDurumu === "REDDEDILDI" && (
                                <label className="block">
                                    <span className="text-xs text-[var(--mbs-ink-3)] mb-1 block">
                                        Red nedeni *
                                    </span>
                                    <textarea
                                        value={redNedeni}
                                        onChange={(e) => setRedNedeni(e.target.value)}
                                        rows={2}
                                        className={PANEL_INPUT}
                                        placeholder="Evrak neden reddedildi?"
                                    />
                                </label>
                            )}
                        </fieldset>

                        <fieldset className="space-y-3">
                            <legend className="text-[10px] uppercase tracking-[0.16em] text-[var(--mbs-primary-hover)] font-semibold">
                                Dosya
                            </legend>
                            {isEdit && mevcutDosyaUrl && !file && (
                                <div className="flex items-center gap-2 text-sm text-[var(--mbs-ink-3)] px-3 py-2 rounded-md border border-[var(--mbs-border)] bg-[var(--mbs-bg-soft)]">
                                    <FileText className="h-4 w-4 shrink-0" />
                                    <span className="truncate flex-1">Mevcut dosya kayıtlı</span>
                                    <a
                                        href={mevcutDosyaUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[var(--mbs-primary-hover)] hover:underline inline-flex items-center gap-1 shrink-0"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                        Aç
                                    </a>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                className="w-full rounded-lg border-2 border-dashed border-[var(--mbs-border)] hover:border-[var(--mbs-primary-hover)]/50 px-4 py-6 text-center transition"
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        setFile(f || null);
                                        if (f) {
                                            setEvrakTuru(
                                                detectEvrakTuruFromFilename(f.name),
                                            );
                                        }
                                        e.target.value = "";
                                    }}
                                />
                                <UploadCloud className="h-8 w-8 mx-auto text-[var(--mbs-primary-hover)] mb-2" />
                                <div className="text-sm text-[var(--mbs-ink)]">
                                    {file ? file.name : isEdit ? "Yeni dosya seç (isteğe bağlı)" : "Dosya seç *"}
                                </div>
                                {file && (
                                    <div className="text-xs text-[var(--mbs-ink-3)] mt-1 space-y-0.5">
                                        <div>{(file.size / 1024).toFixed(0)} KB</div>
                                        <div className="text-[#F3E5AB]">
                                            Tür (dosya adından): {evrakTuruLabel(evrakTuru)}
                                        </div>
                                    </div>
                                )}
                            </button>
                        </fieldset>

                        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--mbs-border)]">
                            <button
                                type="button"
                                onClick={onClose}
                                className="h-9 px-4 rounded-md text-sm border border-[var(--mbs-border)] text-[var(--mbs-ink-3)] hover:bg-[var(--mbs-bg-soft)]"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={busy}
                                className="btn-gold h-9 px-5 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                {busy ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4" />
                                )}
                                {isEdit ? "Kaydet" : "Yükle"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
